/**
 * M24 task-09 — DB connector Show page + schema editor (DBC-03/04; HUB-079
 * §4/§6). Reached by admins from the list AND by Zanzibar-`editor`-granted
 * non-admins via deep link (the route is auth-gated only — see App.tsx).
 *
 * - Admin-only actions (test / sync / rotate / delete / allowSampling /
 *   biPrompt) are hidden for non-admin editors via the SAME role signal
 *   `RequireAdmin` reads: `usePermissions()` + `isAdminRoles`.
 * - The schema editor is visible to admins AND editors; the hub enforces
 *   per-connector access — a 403 renders the no-access state.
 * - DEFAULT view: the active spec as a checkbox tree (checked =
 *   `active !== false`); every toggle saves through the server validator.
 *   A raw-JSON tab (client-side JSON.parse ONLY — the one spec validator
 *   lives on the server) and a versions/presets panel with Activate.
 * - Connection strings are WRITE-ONLY: the rotate field is never prefilled
 *   and no response carries connection-string material.
 */
import { useCallback, useEffect, useState } from 'react';
import { useDelete, usePermissions, useShow } from '@refinedev/core';
import { useNavigate } from 'react-router';
import type {
  DbConnectorItem,
  DbConnectorSchemaResponse,
  DbConnectorSchemaVersion,
  DbConnectorSpec,
  DbConnectorTestResponse,
} from '@verybigthings/trackframe-shared/schemas';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { hub } from '@/lib/hub-client';
import { isAdminRoles } from '@/lib/require-admin';
import { fmtDateTime } from '@/lib/format';
import { ConnectorStatusBadge, SchemaStatusBadge } from './List';
import { isSpecActive, toggleActive, type SpecMemberKind } from './spec-edit';

export const NO_ACCESS_COPY =
  'Connector not found — it may have been deleted, or you do not have access to it.';
export const SCHEMA_NO_ACCESS_COPY = 'You do not have access to this connector’s schema.';
export const SCHEMA_ERROR_HINT =
  'Fix the schema by hand in the editor below, or re-sync to regenerate it.';
export const NO_SCHEMA_COPY =
  'No schema generated yet — sync to generate one from the BI prompt, or save a schema in the raw editor.';
export const SYNC_QUEUED_COPY = 'Sync queued — the schema regenerates from the current BI prompt.';
export const BI_PROMPT_NOTE = 'Applies on next sync.';
export const QUERY_PROMPT_NOTE = 'Applies immediately — passed with every question, no sync needed.';

/** 422 invalid_spec → its `issues` list; anything else → null. */
export function invalidSpecIssues(err: unknown): string[] | null {
  if (!(err instanceof HubClientError) || err.httpStatus !== 422) return null;
  const body = err.body as { issues?: unknown } | undefined;
  return Array.isArray(body?.issues) ? (body.issues as string[]) : [];
}

function isForbidden(err: unknown): boolean {
  return err instanceof HubClientError && err.httpStatus === 403;
}

// --- Pure schema-editor pieces (exported seams for the no-jsdom tests) ---

function MemberCheckbox({
  label,
  detail,
  checked,
  disabled,
  onToggle,
}: {
  label: string;
  detail?: string;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex items-center gap-2 py-0.5 text-sm">
      <input
        type="checkbox"
        aria-label={`toggle ${label}`}
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
      />
      <span className={checked ? '' : 'text-muted-foreground line-through'}>{label}</span>
      {detail && <span className="text-xs text-muted-foreground">{detail}</span>}
    </label>
  );
}

export function SchemaCheckboxTree({
  spec,
  disabled,
  onToggle,
}: {
  spec: DbConnectorSpec;
  disabled: boolean;
  onToggle: (modelName: string, memberKind?: SpecMemberKind, memberName?: string) => void;
}) {
  return (
    <div className="space-y-4">
      {spec.models.map((model) => (
        <div key={model.name} className="rounded-md border p-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              aria-label={`toggle ${model.name}`}
              checked={isSpecActive(model)}
              disabled={disabled}
              onChange={() => onToggle(model.name)}
            />
            <span>{model.name}</span>
            <span className="font-mono text-xs font-normal text-muted-foreground">
              {model.schema}.{model.table}
            </span>
          </label>
          {model.description && (
            <p className="mt-1 pl-6 text-xs text-muted-foreground">{model.description}</p>
          )}
          <div className="mt-2 space-y-2 pl-6">
            {model.dimensions.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Dimensions</p>
                {model.dimensions.map((d) => (
                  <MemberCheckbox
                    key={d.name}
                    label={`${model.name}.${d.name}`}
                    detail={d.type}
                    checked={isSpecActive(d)}
                    disabled={disabled}
                    onToggle={() => onToggle(model.name, 'dimension', d.name)}
                  />
                ))}
              </div>
            )}
            {model.metrics.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Metrics</p>
                {model.metrics.map((m) => (
                  <MemberCheckbox
                    key={m.name}
                    label={`${model.name}.${m.name}`}
                    detail={m.agg}
                    checked={isSpecActive(m)}
                    disabled={disabled}
                    onToggle={() => onToggle(model.name, 'metric', m.name)}
                  />
                ))}
              </div>
            )}
            {(model.categoricalHierarchies ?? []).length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Hierarchies</p>
                {(model.categoricalHierarchies ?? []).map((h) => (
                  <MemberCheckbox
                    key={h.name}
                    label={`${model.name}.${h.name}`}
                    checked={isSpecActive(h)}
                    disabled={disabled}
                    onToggle={() => onToggle(model.name, 'hierarchy', h.name)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function VersionsPanel({
  versions,
  activeVersion,
  busyVersion,
  onActivate,
}: {
  versions: DbConnectorSchemaVersion[];
  activeVersion: number | null;
  busyVersion: number | null;
  onActivate: (version: number) => void;
}) {
  if (versions.length === 0) {
    return <p className="text-sm text-muted-foreground">No saved versions yet.</p>;
  }
  return (
    <ul className="divide-y">
      {versions.map((v) => {
        const isActiveVersion = v.version === activeVersion;
        return (
          <li
            key={v.id}
            className={`flex items-center justify-between gap-2 py-2 ${isActiveVersion ? 'font-medium' : ''}`}
          >
            <div className="min-w-0">
              <span className="tabular-nums">v{v.version}</span>
              <span className="ml-2 truncate">{v.name ?? 'unnamed'}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {v.generatedBy} · {fmtDateTime(v.createdAt)}
              </span>
            </div>
            {isActiveVersion ? (
              <Badge variant="success" className="rounded-full">
                Active
              </Badge>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={busyVersion !== null}
                onClick={() => onActivate(v.version)}
              >
                {busyVersion === v.version ? 'Activating…' : 'Activate'}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// --- Raw editor tab (client-side JSON.parse ONLY — the spec validator is
// the server's; a 422 renders its `issues` list) ---

function RawSpecEditor({
  spec,
  saving,
  onSave,
}: {
  spec: DbConnectorSpec | null;
  saving: boolean;
  onSave: (spec: unknown, saveAs?: string) => void;
}) {
  const [text, setText] = useState(() => (spec ? JSON.stringify(spec, null, 2) : ''));
  const [saveAs, setSaveAs] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  // Re-seed the textarea when a fresh spec lands (save/activate/refetch).
  useEffect(() => {
    setText(spec ? JSON.stringify(spec, null, 2) : '');
  }, [spec]);

  const save = () => {
    setParseError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      setParseError(`Invalid JSON: ${(err as Error).message}`);
      return;
    }
    onSave(parsed, saveAs.trim() ? saveAs.trim() : undefined);
    setSaveAs('');
  };

  return (
    <div className="space-y-3">
      <textarea
        aria-label="Schema spec JSON"
        rows={20}
        spellCheck={false}
        className="w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {parseError && <Alert>{parseError}</Alert>}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save schema'}
        </Button>
        <Input
          aria-label="Save as new version"
          placeholder="Save as new version (optional name)"
          className="h-9 w-72"
          value={saveAs}
          onChange={(e) => setSaveAs(e.target.value)}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Validated on the server on every save. A non-empty name saves (and activates) a new named version.
      </p>
    </div>
  );
}

// --- The page ---

export function DbConnectorsShow() {
  const navigate = useNavigate();
  // The SAME admin signal RequireAdmin consumes (authProvider.getPermissions
  // → me.user.roles) — this page sits OUTSIDE RequireAdmin, so it gates the
  // admin-only actions itself.
  const { data: roles } = usePermissions<unknown>({});
  const isAdmin = isAdminRoles(roles);

  const { query } = useShow<DbConnectorItem>();
  const connector = query.data?.data;
  const connectorId = connector?.id ?? null;

  // --- schema state (direct hub calls; not a refine resource) ---
  const [schema, setSchema] = useState<DbConnectorSchemaResponse | null>(null);
  const [spec, setSpec] = useState<DbConnectorSpec | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [schemaForbidden, setSchemaForbidden] = useState(false);
  const [schemaLoadError, setSchemaLoadError] = useState<string | null>(null);

  const fetchSchema = useCallback(async () => {
    if (!connectorId) return;
    try {
      const res: DbConnectorSchemaResponse = await hub.dbConnectorsSchema({
        params: { id: connectorId },
      });
      setSchema(res);
      setSpec(res.active?.spec ?? null);
      setSchemaForbidden(false);
      setSchemaLoadError(null);
    } catch (err) {
      if (isForbidden(err)) setSchemaForbidden(true);
      else setSchemaLoadError((err as Error).message);
    } finally {
      setSchemaLoading(false);
    }
  }, [connectorId]);

  useEffect(() => {
    void fetchSchema();
  }, [fetchSchema]);

  // --- schema save (shared by the checkbox tree and the raw editor) ---
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<string[] | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const saveSpec = useCallback(
    async (nextSpec: unknown, saveAs?: string) => {
      if (!connectorId) return;
      setSaving(true);
      setIssues(null);
      setSaveError(null);
      try {
        await hub.dbConnectorsSchemaSave({
          params: { id: connectorId },
          body: { spec: nextSpec, ...(saveAs ? { saveAs } : {}) },
        });
        await Promise.all([fetchSchema(), query.refetch()]);
      } catch (err) {
        const specIssues = invalidSpecIssues(err);
        if (specIssues) setIssues(specIssues);
        else if (isForbidden(err)) setSchemaForbidden(true);
        else setSaveError((err as Error).message);
        // Revert optimistic checkbox state to the server's truth.
        await fetchSchema();
      } finally {
        setSaving(false);
      }
    },
    [connectorId, fetchSchema, query],
  );

  const onToggle = (modelName: string, memberKind?: SpecMemberKind, memberName?: string) => {
    if (!spec) return;
    const next = toggleActive(spec, modelName, memberKind, memberName);
    setSpec(next); // optimistic; a 422 refetch-reverts
    void saveSpec(next);
  };

  // --- versions/presets ---
  const [busyVersion, setBusyVersion] = useState<number | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);
  const onActivate = async (version: number) => {
    if (!connectorId) return;
    setBusyVersion(version);
    setActivateError(null);
    try {
      await hub.dbConnectorsSchemaActivate({ params: { id: connectorId }, body: { version } });
      await Promise.all([fetchSchema(), query.refetch()]);
    } catch (err) {
      if (isForbidden(err)) setSchemaForbidden(true);
      else setActivateError((err as Error).message);
    } finally {
      setBusyVersion(null);
    }
  };

  // --- admin-only actions ---
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<DbConnectorTestResponse | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rotateValue, setRotateValue] = useState('');
  const [rotateBusy, setRotateBusy] = useState(false);
  const [rotateNote, setRotateNote] = useState<string | null>(null);
  const [samplingBusy, setSamplingBusy] = useState(false);
  const [biPromptDraft, setBiPromptDraft] = useState<string | null>(null);
  const [biPromptBusy, setBiPromptBusy] = useState(false);
  const [queryPromptDraft, setQueryPromptDraft] = useState<string | null>(null);
  const [queryPromptBusy, setQueryPromptBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { mutate: deleteConnector, mutation: deleteMutation } = useDelete();
  const deleting = deleteMutation.isPending;

  const runTest = async () => {
    if (!connectorId) return;
    setTestBusy(true);
    setActionError(null);
    setTestResult(null);
    try {
      setTestResult(await hub.dbConnectorsTest({ params: { id: connectorId } }));
      await query.refetch();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setTestBusy(false);
    }
  };

  const runSync = async () => {
    if (!connectorId) return;
    setSyncBusy(true);
    setActionError(null);
    setSyncNote(null);
    try {
      await hub.dbConnectorsSync({ params: { id: connectorId } });
      setSyncNote(SYNC_QUEUED_COPY);
      await query.refetch();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setSyncBusy(false);
    }
  };

  const rotate = async () => {
    if (!connectorId || !rotateValue) return;
    setRotateBusy(true);
    setActionError(null);
    setRotateNote(null);
    try {
      await hub.dbConnectorsUpdate({
        params: { id: connectorId },
        body: { connectionString: rotateValue },
      });
      setRotateValue('');
      setRotateNote('Connection string updated.');
      await query.refetch();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setRotateBusy(false);
    }
  };

  const setAllowSampling = async (allowSampling: boolean) => {
    if (!connectorId) return;
    setSamplingBusy(true);
    setActionError(null);
    try {
      await hub.dbConnectorsUpdate({ params: { id: connectorId }, body: { allowSampling } });
      await query.refetch();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setSamplingBusy(false);
    }
  };

  const saveBiPrompt = async () => {
    if (!connectorId || biPromptDraft === null) return;
    setBiPromptBusy(true);
    setActionError(null);
    try {
      await hub.dbConnectorsUpdate({
        params: { id: connectorId },
        body: { biPrompt: biPromptDraft.trim() ? biPromptDraft : null },
      });
      setBiPromptDraft(null);
      await query.refetch();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBiPromptBusy(false);
    }
  };

  const saveQueryPrompt = async () => {
    if (!connectorId || queryPromptDraft === null) return;
    setQueryPromptBusy(true);
    setActionError(null);
    try {
      await hub.dbConnectorsUpdate({
        params: { id: connectorId },
        body: { queryPrompt: queryPromptDraft.trim() ? queryPromptDraft : null },
      });
      setQueryPromptDraft(null);
      await query.refetch();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setQueryPromptBusy(false);
    }
  };

  const onDelete = () => {
    if (!connectorId) return;
    deleteConnector(
      { resource: 'db-connectors', id: connectorId },
      { onSuccess: () => navigate('/admin/db-connectors') },
    );
  };

  const refresh = () => {
    void query.refetch();
    void fetchSchema();
  };

  if (query.isLoading) return <div>Loading…</div>;
  // getOne derives from the list — an editor without access (or a deleted
  // connector) simply isn't in it. The server is the actual gate.
  if (!connector) return <Alert>{NO_ACCESS_COPY}</Alert>;

  const activeVersion = schema?.active?.version ?? connector.activeSchemaVersion;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{connector.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <code className="font-mono text-xs">{connector.engine}</code>
            <ConnectorStatusBadge status={connector.status} />
            <SchemaStatusBadge status={connector.schemaStatus} />
            <span>
              {connector.activeSchemaVersion === null ? 'no active schema' : `active v${connector.activeSchemaVersion}`}
            </span>
            <span>· last synced {fmtDateTime(connector.lastSyncedAt)}</span>
          </div>
        </div>
        <Button variant="outline" onClick={refresh}>
          Refresh
        </Button>
      </div>

      {connector.schemaStatus === 'error' && (
        <Alert>
          <p className="font-medium">Schema generation failed{connector.lastError ? `: ${connector.lastError}` : '.'}</p>
          <p className="mt-1">{SCHEMA_ERROR_HINT}</p>
        </Alert>
      )}

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={runTest} disabled={testBusy}>
                {testBusy ? 'Testing…' : 'Test connection'}
              </Button>
              <Button variant="outline" onClick={runSync} disabled={syncBusy}>
                {syncBusy ? 'Queuing…' : 'Sync schema'}
              </Button>
              <Button variant="destructive" onClick={() => setConfirmDelete(true)} disabled={deleting}>
                Delete
              </Button>
            </div>
            {testResult &&
              (testResult.ok ? (
                <p className="text-sm text-foreground" role="status">
                  Connection OK.
                </p>
              ) : (
                <Alert>Unreachable{testResult.error ? `: ${testResult.error}` : '.'}</Alert>
              ))}
            {syncNote && (
              <p className="text-sm text-foreground" role="status">
                {syncNote}
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="rotate">Rotate connection string</Label>
              <div className="flex flex-wrap items-center gap-2">
                {/* Write-only: never prefilled, never displayed back. */}
                <Input
                  id="rotate"
                  type="password"
                  autoComplete="off"
                  placeholder="New connection string"
                  className="w-96"
                  value={rotateValue}
                  onChange={(e) => setRotateValue(e.target.value)}
                />
                <Button variant="outline" onClick={rotate} disabled={rotateBusy || !rotateValue}>
                  {rotateBusy ? 'Saving…' : 'Save'}
                </Button>
              </div>
              {rotateNote && (
                <p className="text-sm text-foreground" role="status">
                  {rotateNote}
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={connector.allowSampling}
                disabled={samplingBusy}
                onChange={(e) => void setAllowSampling(e.target.checked)}
              />
              Allow sampling (lets generation read a few example rows)
            </label>

            <div className="space-y-2">
              <Label htmlFor="biPrompt">BI prompt</Label>
              <textarea
                id="biPrompt"
                rows={4}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="What matters from a BI perspective? Dimensions and facts…"
                value={biPromptDraft ?? connector.biPrompt ?? ''}
                onChange={(e) => setBiPromptDraft(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={saveBiPrompt}
                  disabled={biPromptBusy || biPromptDraft === null}
                >
                  {biPromptBusy ? 'Saving…' : 'Save BI prompt'}
                </Button>
                <span className="text-xs text-muted-foreground">{BI_PROMPT_NOTE}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="queryPrompt">Query prompt</Label>
              <textarea
                id="queryPrompt"
                rows={4}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Domain facts for query writing — valid codes, aliases, defaults. e.g. For mill use only STT and STP, default is STT."
                value={queryPromptDraft ?? connector.queryPrompt ?? ''}
                onChange={(e) => setQueryPromptDraft(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={saveQueryPrompt}
                  disabled={queryPromptBusy || queryPromptDraft === null}
                >
                  {queryPromptBusy ? 'Saving…' : 'Save query prompt'}
                </Button>
                <span className="text-xs text-muted-foreground">{QUERY_PROMPT_NOTE}</span>
              </div>
            </div>

            {actionError && <Alert>{actionError}</Alert>}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Schema</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {schemaForbidden ? (
            <Alert>{SCHEMA_NO_ACCESS_COPY}</Alert>
          ) : schemaLoading ? (
            <p className="text-sm text-muted-foreground">Loading schema…</p>
          ) : schemaLoadError ? (
            <Alert>{schemaLoadError}</Alert>
          ) : (
            <>
              {issues && (
                <Alert>
                  <p className="font-medium">The server rejected the schema:</p>
                  <ul className="mt-1 list-disc pl-5">
                    {issues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              {saveError && <Alert>{saveError}</Alert>}

              {schema?.active == null ? (
                <>
                  <p className="text-sm text-muted-foreground">{NO_SCHEMA_COPY}</p>
                  <RawSpecEditor spec={null} saving={saving} onSave={(s, saveAs) => void saveSpec(s, saveAs)} />
                </>
              ) : (
                <Tabs defaultValue="checkboxes">
                  <TabsList>
                    <TabsTrigger value="checkboxes">Tables &amp; fields</TabsTrigger>
                    <TabsTrigger value="raw">Raw JSON</TabsTrigger>
                  </TabsList>
                  <TabsContent value="checkboxes">
                    {spec && <SchemaCheckboxTree spec={spec} disabled={saving} onToggle={onToggle} />}
                  </TabsContent>
                  <TabsContent value="raw">
                    <RawSpecEditor spec={spec} saving={saving} onSave={(s, saveAs) => void saveSpec(s, saveAs)} />
                  </TabsContent>
                </Tabs>
              )}

              <div className="border-t pt-4">
                <h2 className="mb-2 text-sm font-medium">Versions</h2>
                {activateError && <Alert className="mb-2">{activateError}</Alert>}
                <VersionsPanel
                  versions={schema?.versions ?? []}
                  activeVersion={activeVersion}
                  busyVersion={busyVersion}
                  onActivate={(v) => void onActivate(v)}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete connector?"
        description={`"${connector.name}" and all of its schema versions will be removed.`}
        confirmLabel="Delete"
        destructive
        busy={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onDelete}
      />
    </div>
  );
}
